import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  ModuleHeader,
  WarningMessageBox,
} from '@xing-com/crate-entity-pages-common';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import {
  IdealEmployerDocument,
  IdealEmployer,
} from '@xing-com/crate-entity-pages-ideal-employer';

import { BRANDMANAGER_URL } from '../../../config/urls';

import { IdealEmployerSkeleton } from './ideal-employer-module-skeleton';

const ErrorFallback: FC<{ onReloadClick?: () => void }> = ({
  onReloadClick = () => window.location.reload(),
}) => {
  return (
    <div data-testid={'errorContainer'}>
      <WarningMessageBox
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={() => onReloadClick()}
      />
    </div>
  );
};

const IdealEmployerModuleInner: React.FC = () => {
  const { pageContext } = usePageContext();

  const { data, error, refetch } = useSuspenseQuery(IdealEmployerDocument, {
    variables: { id: pageContext.companyId ?? '' },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  return (
    <>
      {error ? (
        <div data-testid={'errorContainer'}>
          <ErrorFallback onReloadClick={() => refetch()} />
        </div>
      ) : null}
      {data && !error ? (
        <IdealEmployer
          isIdealEmployer={!!data?.company?.proJobs?.idealEmployer}
        />
      ) : null}
    </>
  );
};

export const IdealEmployerModule: React.FC = () => {
  const { pageContext } = usePageContext();
  const isEditing = useEditStore(({ isEditing }) => isEditing);

  return (
    <>
      <ModuleHeader
        headlineCopyKey="EP_IDEAL_EMPLOYER_HEADLINE"
        externalEditInfo={
          isEditing
            ? {
                copyKey: 'EP_IDEAL_EMPLOYER_EDIT_HINT',
                ctaCopyKey: 'EP_IDEAL_EMPLOYER_EDIT_HINT_CTA',
                ctaUrl: BRANDMANAGER_URL(pageContext.pageSlug) + '/page-reach',
                cyTestId: 'EDIT_HINT_IDEAL_EMPLOYER',
                ctaAvailableInMobile: false,
              }
            : null
        }
      />
      <ErrorBoundary onError={() => <ErrorFallback />}>
        <Suspense fallback={<IdealEmployerSkeleton />}>
          <IdealEmployerModuleInner />
        </Suspense>
      </ErrorBoundary>
    </>
  );
};
